import drawFragSrc from "../gl/glsl/draw.frag";
import drawVertSrc from "../gl/glsl/draw.vert";
import fullscreenVertSrc from "../gl/glsl/fullscreen.vert";
import hashSrc from "../gl/glsl/hash.glsl";
import presentFragSrc from "../gl/glsl/present.frag";
import { createFramebuffer, createRenderTexture } from "../gl/buffers";
import { compileProgram, type LinkedProgram } from "../gl/shader";
import { MAX_STOPS } from "../palettes";
import { BLEED } from "../sim/state";

export type Rgb = [number, number, number];

export interface RenderPrograms {
  draw: LinkedProgram;
  present: LinkedProgram;
}

export function createRenderPrograms(
  gl: WebGL2RenderingContext,
): RenderPrograms {
  return {
    draw: compileProgram(gl, {
      vert: drawVertSrc,
      frag: drawFragSrc,
      defines: { MAX_STOPS },
      attribLocations: { a_posAgeLife: 0, a_rand: 1 },
    }),
    present: compileProgram(gl, {
      vert: fullscreenVertSrc,
      frag: presentFragSrc,
      fragChunks: [hashSrc],
    }),
  };
}

export function destroyRenderPrograms(
  gl: WebGL2RenderingContext,
  programs: RenderPrograms,
): void {
  gl.deleteProgram(programs.draw.program);
  gl.deleteProgram(programs.present.program);
}

export interface StepUniforms {
  bg: Rgb; // linear-light
  aspect: number;
  stops: Float32Array; // MAX_STOPS x vec3, linear-light, padded
  stopCount: number;
  dotSize: number; // CSS px
  resScale: number; // devicePixels per CSS px (export: exportH / cssH)
  zoom: number; // >= 1: view magnification (dots scale with it)
  sizeJitter: number;
  dotOpacity: number;
  glow: number;
  symmetry: number;
}

/**
 * Owns one persistent trail FBO and the dots -> present pipeline. Ink is
 * PERMANENT: dots accumulate additively and never fade — clearTrail (clear
 * to bg) is the only way ink disappears. The exporter creates a second
 * instance at export resolution, sharing the same compiled programs.
 */
export class Renderer {
  width = 0;
  height = 0;
  /** May downgrade to false if the 16F FBO turns out incomplete. */
  usesFloatTrail: boolean;

  private tex: WebGLTexture | null = null;
  private fbo: WebGLFramebuffer | null = null;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly programs: RenderPrograms,
    preferFloat: boolean,
    private readonly maxPointSize: number,
  ) {
    this.usesFloatTrail = preferFloat;
  }

  /** (Re)allocate the trail buffer and clear it to the background. */
  setSize(width: number, height: number, bg: Rgb): void {
    const gl = this.gl;
    this.freeTarget();
    this.width = width;
    this.height = height;
    if (this.usesFloatTrail) {
      this.tex = createRenderTexture(gl, width, height, true);
      this.fbo = createFramebuffer(gl, this.tex);
      if (!this.fbo) {
        // Extension present but attachment incomplete — fall back to RGBA8.
        // Accumulation has no decay floor, so the only cost is mild banding.
        gl.deleteTexture(this.tex);
        this.tex = null;
        this.usesFloatTrail = false;
      }
    }
    if (!this.fbo) {
      this.tex = createRenderTexture(gl, width, height, false);
      this.fbo = createFramebuffer(gl, this.tex);
      if (!this.fbo) throw new Error("trail framebuffer incomplete");
    }
    this.clearTrail(bg);
  }

  clearTrail(bg: Rgb): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.clearColor(bg[0], bg[1], bg[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Per sim step: deposit dots additively into the permanent trail. */
  drawStep(
    vao: WebGLVertexArrayObject,
    count: number,
    u: StepUniforms,
  ): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.viewport(0, 0, this.width, this.height);
    gl.enable(gl.BLEND);

    const draw = this.programs.draw;
    gl.useProgram(draw.program);
    gl.uniform2f(draw.uniform("u_domain"), u.aspect + 2 * BLEED, 1 + 2 * BLEED);
    gl.uniform2f(draw.uniform("u_view"), u.aspect, 1);
    gl.uniform1f(draw.uniform("u_dotSize"), u.dotSize);
    gl.uniform1f(draw.uniform("u_resScale"), u.resScale);
    gl.uniform1f(draw.uniform("u_zoom"), u.zoom);
    gl.uniform1f(draw.uniform("u_sizeJitter"), u.sizeJitter);
    gl.uniform1f(draw.uniform("u_maxPointSize"), this.maxPointSize);
    gl.uniform1i(draw.uniform("u_symmetry"), u.symmetry);
    gl.uniform3fv(draw.uniform("u_stops"), u.stops);
    gl.uniform1i(draw.uniform("u_stopCount"), u.stopCount);
    gl.uniform1f(draw.uniform("u_glow"), u.glow);
    gl.uniform1f(draw.uniform("u_dotOpacity"), u.dotOpacity);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.bindVertexArray(vao);
    gl.drawArraysInstanced(gl.POINTS, 0, count, Math.max(1, u.symmetry));
    gl.bindVertexArray(null);

    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Tonemap + gamma + dither into `target` (null = default framebuffer). */
  present(
    target: WebGLFramebuffer | null,
    exposure: number,
    frame: number,
  ): void {
    const gl = this.gl;
    const present = this.programs.present;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target);
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.BLEND);
    gl.useProgram(present.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.uniform1i(present.uniform("u_trail"), 0);
    gl.uniform1f(present.uniform("u_exposure"), exposure);
    gl.uniform1ui(present.uniform("u_frame"), frame >>> 0);
    gl.bindVertexArray(null);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    // Unbind so the trail texture is never simultaneously sampled and
    // FBO-attached during the next drawStep (feedback loop = undefined).
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private freeTarget(): void {
    const gl = this.gl;
    if (this.fbo) gl.deleteFramebuffer(this.fbo);
    if (this.tex) gl.deleteTexture(this.tex);
    this.fbo = null;
    this.tex = null;
  }

  destroy(): void {
    this.freeTarget();
  }
}
